import React, { useMemo, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import type { AgGridReactProps } from 'ag-grid-react';
import {
  AllCommunityModule,
  ModuleRegistry,
  ColDef,
  SortChangedEvent,
  PaginationChangedEvent,
  themeQuartz,
} from 'ag-grid-community';

ModuleRegistry.registerModules([AllCommunityModule]);

// Quartz with see-through backgrounds, so the grid takes on the glass card's tint
// instead of sitting on it as a white block. Header/zebra/hover are light white or
// navy washes rather than solid colours for the same reason.
const glassGridTheme = themeQuartz.withParams({
  backgroundColor: 'transparent',
  chromeBackgroundColor: 'transparent',
  // Solid (pre-blended 45% white wash) so scrolled cells never show through the header.
  headerBackgroundColor: '#F8FAFE',
  oddRowBackgroundColor: 'rgba(255, 255, 255, 0.25)',
  rowHoverColor: 'rgba(21, 41, 92, 0.05)',
  wrapperBorder: { color: 'rgba(255, 255, 255, 0.9)' },
  // Popups inherit backgroundColor by default - keep them solid so filter menus,
  // dropdowns, tooltips and cell editors stay readable over the rows.
  menuBackgroundColor: '#FFFFFF',
  panelBackgroundColor: '#FFFFFF',
  pickerListBackgroundColor: '#FFFFFF',
  tooltipBackgroundColor: '#FFFFFF',
  inputBackgroundColor: '#FFFFFF',
  selectCellBackgroundColor: '#FFFFFF',
});

export interface DataTableProps<T> {
  rowData: T[];
  columnDefs: ColDef<T>[];
  getRowId: (data: T) => string;
  defaultColDef?: ColDef<T>;
  editType?: 'fullRow';
  enableSearch?: boolean;
  searchPlaceholder?: string;
  /**
   * Controlled search value. Pass this + onSearchChange when a parent needs to observe/own the
   * search text (e.g. to later drive a server-side search request). If omitted, the table manages
   * its own search state internally (current client-side-only behavior).
   */
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  pagination?: boolean;
  paginationPageSize?: number;
  paginationPageSizeSelector?: number[];
  /**
   * Notified whenever sorting changes. Sorting itself still happens client-side today; this is the
   * hook a future server-side conversion would use to refetch with a sort param instead.
   */
  onSortChanged?: (event: SortChangedEvent<T>) => void;
  /**
   * Notified whenever the page or page size changes. Pagination itself still happens client-side
   * today; this is the hook a future server-side conversion would use to refetch the requested page.
   */
  onPaginationChanged?: (event: PaginationChangedEvent<T>) => void;
  domLayout?: 'normal' | 'autoHeight' | 'print';
  height?: string | number;
  themeClassName?: string;
  /** Escape hatch: any other AgGridReact/GridOptions prop not covered above (e.g. onCellValueChanged, sideBar, rowSelection, etc.) */
  gridProps?: Partial<AgGridReactProps<T>>;
}

export default function DataTable<T>({
  rowData,
  columnDefs,
  getRowId,
  defaultColDef,
  editType,
  enableSearch = true,
  searchPlaceholder = 'Search...',
  searchValue,
  onSearchChange,
  pagination = true,
  paginationPageSize = 10,
  paginationPageSizeSelector = [10, 20, 50, 100],
  onSortChanged,
  onPaginationChanged,
  domLayout = 'autoHeight',
  height,
  themeClassName = 'ag-theme-quartz',
  gridProps,
}: DataTableProps<T>) {
  const [internalQuickFilterText, setInternalQuickFilterText] = useState('');
  const isSearchControlled = searchValue !== undefined;
  const quickFilterText = isSearchControlled ? searchValue : internalQuickFilterText;

  const handleSearchInputChange = (value: string) => {
    if (!isSearchControlled) setInternalQuickFilterText(value);
    onSearchChange?.(value);
  };

  const mergedDefaultColDef = useMemo<ColDef<T>>(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      tooltipValueGetter: (params) =>
        params.value !== undefined && params.value !== null && params.value !== '' ? String(params.value) : null,
      ...defaultColDef,
    }),
    [defaultColDef]
  );

  return (
    <div>
      {enableSearch && (
        <div style={{ marginBottom: '12px', maxWidth: '320px' }}>
          <input
            className="form-input"
            type="text"
            value={quickFilterText}
            onChange={(e) => handleSearchInputChange(e.target.value)}
            placeholder={searchPlaceholder}
          />
        </div>
      )}

      <div
        className={themeClassName}
        style={{
          width: '100%',
          height: domLayout === 'normal' ? height : undefined,
        }}
      >
        <AgGridReact<T>
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={mergedDefaultColDef}
          getRowId={(params) => getRowId(params.data)}
          quickFilterText={quickFilterText}
          editType={editType}
          domLayout={domLayout}
          pagination={pagination}
          paginationPageSize={paginationPageSize}
          paginationPageSizeSelector={paginationPageSizeSelector}
          onSortChanged={onSortChanged}
          onPaginationChanged={onPaginationChanged}
          theme={glassGridTheme}
          {...gridProps}
        />
      </div>
    </div>
  );
}
